import Phaser from 'phaser';
import {
  COLLISION_CORE_THICKNESS_RATIO,
  COMMON_DENSITY,
  DROP_SPAWN_Y,
  DROP_CLEARANCE,
  FRICTION,
  FRICTION_AIR,
  FRICTION_STATIC,
  FIELD_HEIGHT,
  FIELD_WIDTH,
  RESTITUTION,
} from '../../config/gameplay';
import { getCharacterForPhase, type CharacterId } from '../../game/content/characters';
import type { ResonancePhase } from '../../game/simulation/dropBag';
import { DEFAULT_GAME_MODE, type GameModeId } from '../../game/content/modes';
import { minimumPartThickness, normalizedPartsToLocal, polygonCentroid, type Point } from '../../game/geometry';
import { dangerCoreTopY } from './dangerCore';
import { maxMatterSpeedForThickness } from './fixedStep';

type BodyMetadata = {
  entityId: string;
  generation: number;
  revision: number;
  maxMatterSpeed: number;
};

export type CharacterBody = Readonly<{
  entityId: string;
  characterId: CharacterId;
  image: Phaser.Physics.Matter.Image;
  body: MatterJS.BodyType;
  baseMass: number;
}>;

const DEFAULT_COLLISION_MASK = 0xffff_ffff;

export class BodyFactory {
  private readonly bodies = new Map<string, CharacterBody>();
  private bodyRevision = 0;
  private phase: ResonancePhase = 1;
  private mode: GameModeId = DEFAULT_GAME_MODE;

  public constructor(private readonly scene: Phaser.Scene) {}

  /** Set before applying a batch: phase transitions recreate the sole survivor. */
  public setPhase(phase: ResonancePhase, mode: GameModeId = DEFAULT_GAME_MODE): void {
    this.phase = phase;
    this.mode = mode;
  }

  public characterDefinition(characterId: CharacterId) {
    return getCharacterForPhase(characterId, this.phase, this.mode);
  }

  public create(
    entityId: string,
    characterId: CharacterId,
    x: number,
    y: number,
    generation: number,
    staged: boolean,
    initialVelocity?: Point,
  ): CharacterBody {
    if (this.bodies.has(entityId)) throw new Error(`Body already exists for ${entityId}`);
    const character = this.characterDefinition(characterId);
    const vertexSets = normalizedPartsToLocal(
      character.collisionParts,
      character.centerOfMass,
      character.baseSize,
    );
    const bodyOptions = {
      label: `character:${characterId}`,
      density: COMMON_DENSITY,
      restitution: RESTITUTION,
      friction: FRICTION,
      frictionStatic: FRICTION_STATIC,
      frictionAir: FRICTION_AIR,
      sleepThreshold: 45,
    };
    // Bodies.fromVertices recenters every convex set at the supplied x/y. Passing
    // all sets at once therefore stacks unrelated parts at one point. Build each
    // part at its own centroid first so the shipped-image silhouette survives in
    // the actual Matter compound.
    const partBodies = vertexSets.map((vertices) => {
      const center = polygonCentroid(vertices);
      return this.scene.matter.bodies.fromVertices(
        center.x,
        center.y,
        [[...vertices]],
        bodyOptions,
        true,
        0.01,
        2,
      );
    });
    const body = this.scene.matter.body.create({ ...bodyOptions, parts: partBodies });
    this.scene.matter.body.setPosition(body, { x, y });
    this.scene.matter.body.setDensity(body, COMMON_DENSITY);
    this.scene.matter.body.setAngle(body, character.initialAngle);

    const image = this.scene.matter.add.image(x, y, character.textureKey);
    image.setDisplaySize(character.baseSize, character.baseSize);
    image.setExistingBody(body, true);
    // setExistingBody derives an origin from Matter's compound bounds. Override
    // it with the same catalog center used to translate the collision vertices.
    image.setOrigin(0.5 + character.centerOfMass.x, 0.5 + character.centerOfMass.y);
    image.setDepth(20 + character.stage);

    const minimumThickness = minimumPartThickness(character.collisionParts, character.baseSize);
    const effectiveThickness = Math.max(
      minimumThickness,
      character.baseSize * COLLISION_CORE_THICKNESS_RATIO,
    );
    const maxMatterSpeed = maxMatterSpeedForThickness(effectiveThickness);
    const metadata: BodyMetadata = { entityId, generation, revision: ++this.bodyRevision, maxMatterSpeed };
    body.plugin = { ...(body.plugin ?? {}), ishi: metadata };
    for (const part of body.parts) part.plugin = { ...(part.plugin ?? {}), ishi: metadata };

    if (initialVelocity) this.scene.matter.body.setVelocity(body, initialVelocity);

    if (staged) {
      this.scene.matter.body.setStatic(body, true);
      body.collisionFilter.mask = 0;
      image.setVisible(false);
      image.setAlpha(0);
    }

    const created = { entityId, characterId, image, body, baseMass: body.mass };
    this.bodies.set(entityId, created);
    return created;
  }

  public remove(entityId: string): void {
    const existing = this.bodies.get(entityId);
    if (!existing) return;
    this.bodies.delete(entityId);
    existing.image.destroy();
  }

  public activate(entityId: string): void {
    const existing = this.bodies.get(entityId);
    if (!existing) return;
    this.scene.matter.body.setStatic(existing.body, false);
    existing.body.collisionFilter.mask = DEFAULT_COLLISION_MASK;
    existing.image.setVisible(true);
    existing.image.setAlpha(1);
  }

  public revealStaged(entityId: string): void {
    const existing = this.bodies.get(entityId);
    if (!existing) return;
    const targetScaleX = existing.image.scaleX;
    const targetScaleY = existing.image.scaleY;
    existing.image.setVisible(true);
    existing.image.setAlpha(0);
    existing.image.setScale(targetScaleX * 0.55, targetScaleY * 0.55);
    this.scene.tweens.add({
      targets: existing.image,
      alpha: 1,
      scaleX: targetScaleX,
      scaleY: targetScaleY,
      duration: 360,
      ease: 'Back.Out',
    });
  }

  public reposition(entityId: string, x: number, y: number): void {
    const existing = this.bodies.get(entityId);
    if (!existing) return;
    this.scene.matter.body.setPosition(existing.body, { x, y });
    this.scene.matter.body.setVelocity(existing.body, { x: 0, y: 0 });
    this.scene.matter.body.setAngularVelocity(existing.body, 0);
  }

  public replace(entityId: string, characterId: CharacterId, generation: number, massMultiplier: number): void {
    const existing = this.bodies.get(entityId);
    if (!existing) return;
    const position = { ...existing.body.position };
    const velocity = { ...existing.body.velocity };
    const angle = existing.body.angle;
    const angularVelocity = existing.body.angularVelocity;
    this.remove(entityId);
    const replacement = this.create(entityId, characterId, position.x, position.y, generation, false, velocity);
    this.scene.matter.body.setAngle(replacement.body, angle);
    this.scene.matter.body.setAngularVelocity(replacement.body, angularVelocity);
    this.setMassMultiplier(entityId, massMultiplier);
  }

  public setMassMultiplier(entityId: string, multiplier: number): void {
    const existing = this.bodies.get(entityId);
    if (!existing || !Number.isFinite(multiplier) || multiplier <= 0) return;
    this.scene.matter.body.setMass(existing.body, existing.baseMass * multiplier);
  }

  public zeroUpwardVelocity(entityId: string): void {
    const existing = this.bodies.get(entityId);
    if (!existing || existing.body.velocity.y >= 0) return;
    this.scene.matter.body.setVelocity(existing.body, { x: existing.body.velocity.x, y: 0 });
  }

  public applyBuoyancy(entityId: string): void {
    const existing = this.bodies.get(entityId);
    if (!existing || existing.body.isStatic) return;
    this.scene.matter.body.applyForce(existing.body, existing.body.position, { x: 0, y: -existing.body.mass * 0.00153 });
  }

  public applyAttraction(sourceEntityId: string, targetEntityId: string): void {
    const source = this.bodies.get(sourceEntityId);
    const target = this.bodies.get(targetEntityId);
    if (!source || !target || source.body.isStatic) return;
    // Aim at the underside, rather than accelerating through the target's center.
    // Use vertices instead of velocity-expanded bounds to keep the resting point stable.
    const sourceTop = Math.min(...(source.body.vertices ?? []).map(vertex => vertex.y));
    const targetBottom = Math.max(...(target.body.vertices ?? []).map(vertex => vertex.y));
    const dx = target.body.position.x - source.body.position.x;
    const dy = targetBottom - sourceTop;
    const distance = Math.hypot(dx, dy);
    if (!Number.isFinite(distance)) return;
    // A bounded approach speed falls to zero at contact, with no stored momentum.
    const gain = distance > 0 ? Math.min(0.18, 6 / distance) : 0;
    source.image.setAwake();
    this.scene.matter.body.setVelocity(source.body, {
      x: target.body.velocity.x + dx * gain,
      y: target.body.velocity.y + dy * gain,
    });
    this.scene.matter.body.setAngularVelocity(source.body, 0);
    // Cancel only the world's gravity for this step; normal gravity resumes as
    // soon as the effect expires or either entity disappears.
    const gravity = this.scene.matter.world.localWorld.gravity;
    this.scene.matter.body.applyForce(source.body, source.body.position, {
      x: -source.body.mass * gravity.x * gravity.scale,
      y: -source.body.mass * gravity.y * gravity.scale,
    });
  }

  public applyWind(entityId: string, direction: 'left' | 'right'): void {
    const existing = this.bodies.get(entityId);
    if (!existing || existing.body.isStatic) return;
    const velocity = existing.body.velocity;
    this.scene.matter.body.setVelocity(existing.body, {
      x: velocity.x + (direction === 'left' ? -1.2 : 1.2),
      y: velocity.y,
    });
    this.clampSpeeds();
  }

  public safeReplacementCandidates(entityId: string, candidateIds: readonly CharacterId[]): readonly CharacterId[] {
    const existing = this.bodies.get(entityId);
    if (!existing) return [];
    return candidateIds.filter((characterId) => this.isSafeAt(characterId, existing.body.position.x, existing.body.position.y, entityId));
  }

  public safeDropPositions(characterId: CharacterId, y: number): readonly number[] {
    const character = this.characterDefinition(characterId);
    const margin = character.baseSize / 2 + 4;
    const positions: number[] = [];
    for (let x = Math.ceil(margin); x <= Math.floor(FIELD_WIDTH - margin); x += 1) {
      if (this.isSafeAt(characterId, x, y)) positions.push(x);
    }
    return positions;
  }

  public clampSpeeds(): void {
    for (const existing of this.bodies.values()) {
      if (existing.body.isStatic) continue;
      const metadata = readMetadata(existing.body);
      if (!metadata) continue;
      const speed = Math.hypot(existing.body.velocity.x, existing.body.velocity.y);
      if (speed <= metadata.maxMatterSpeed || speed === 0) continue;
      const ratio = metadata.maxMatterSpeed / speed;
      this.scene.matter.body.setVelocity(existing.body, {
        x: existing.body.velocity.x * ratio,
        y: existing.body.velocity.y * ratio,
      });
    }
  }

  public escapedEntityIds(): readonly string[] {
    return this.all().filter(({ body }) => !body.isStatic && (
      body.bounds.min.y > FIELD_HEIGHT + 24 ||
      (body.position.y > 0 && (body.bounds.max.x < -24 || body.bounds.min.x > FIELD_WIDTH + 24))
    )).map(({ entityId }) => entityId);
  }

  public dropY(characterId: CharacterId, excludeEntityId?: string, attractedEntityId?: string): number {
    const top = this.visibleTop(excludeEntityId, attractedEntityId);
    const character = this.characterDefinition(characterId);
    // New pieces start in the authored orientation. Use their lower extent
    // rather than a diagonal radius that also counts empty side padding.
    const lowerExtent = character.baseSize * (0.5 - character.centerOfMass.y);
    return Math.min(DROP_SPAWN_Y, top - lowerExtent - DROP_CLEARANCE);
  }

  /** Include the entire rotated artwork, not just its collision core. */
  public visibleTop(excludeEntityId?: string, attractedEntityId?: string): number {
    return Math.min(FIELD_HEIGHT, ...this.all()
      .filter(({ entityId, image }) => entityId !== excludeEntityId && entityId !== attractedEntityId && image.visible)
      .map(({ body, image }) => Math.min(body.bounds.min.y, image.getBounds().top)));
  }

  public dangerSamples(lineY: number): readonly Readonly<{ entityId: string; coreAboveLine: boolean }>[] {
    return [...this.bodies.values()].map((existing) => {
      const character = this.characterDefinition(existing.characterId);
      return {
        entityId: existing.entityId,
        coreAboveLine:
          dangerCoreTopY(
            existing.body.position.y,
            existing.body.angle,
            character.baseSize,
            character.dangerCore,
          ) <= lineY,
      };
    });
  }

  public get(entityId: string): CharacterBody | undefined {
    return this.bodies.get(entityId);
  }

  public all(): readonly CharacterBody[] {
    return [...this.bodies.values()];
  }

  public clear(): void {
    for (const entityId of [...this.bodies.keys()]) this.remove(entityId);
  }

  private isSafeAt(characterId: CharacterId, x: number, y: number, ignoredEntityId?: string): boolean {
    const character = this.characterDefinition(characterId);
    const half = character.baseSize / 2;
    if (x - half < 0 || x + half > FIELD_WIDTH || y + half > FIELD_HEIGHT) return false;
    const left = x - half;
    const right = x + half;
    const top = y - half;
    const bottom = y + half;
    return [...this.bodies.values()].every((existing) => {
      if (existing.entityId === ignoredEntityId) return true;
      const bounds = existing.body.bounds;
      return right <= bounds.min.x || left >= bounds.max.x || bottom <= bounds.min.y || top >= bounds.max.y;
    });
  }
}

export function readMetadata(body: MatterJS.BodyType): BodyMetadata | null {
  const root = body.parent && body.parent !== body ? body.parent : body;
  const plugin = root.plugin as { ishi?: BodyMetadata } | undefined;
  return plugin?.ishi ?? null;
}
